import React from "react";
import { Popover, Tooltip } from "antd";
import { FlagFilled, MessageFilled } from "@ant-design/icons";

// The Status grid's styles live with the rest of the patient-board CSS. Imported
// here (rather than relying on the board being loaded first) so this cell renders
// correctly wherever it's mounted — notably the FTC Tasks table.
import "../style.css";

// "Mon D, YYYY" — full date used in the per-policy popover.
function formatActivityDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// "Mon D" — compact, year-less date for the inline Status tokens (keeps the
// column narrow; the year lives in the popover).
function formatShortDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Per-policy Status token. Colour encodes STATE, not type — a single calm
// green for "done" (breakdown verified / estimate available), amber for
// requested, a faint dash for nothing. Which column (Breakdown vs Estimate) a
// token sits in tells you the type, so colour is free to carry state only.
const STATUS_META = {
  verified: { cls: "done", word: "Verified", icon: "✓" },
  available: { cls: "done", word: "Available", icon: "✓" },
  requested: { cls: "req", word: "Requested", icon: null },
  // Waiting on the office is a gold chip (the Details Needed by Smilepass colour)
  // carrying the date it was flagged or handed back. The word follows the
  // reason the server sends for an estimate / claim item (see REASON_WORD).
  attention: { cls: "attn", word: "Details needed", icon: "⚑" },
};

// An estimate / claim token in the attention state says why it waits on the
// office (serializers.policy_statuses_for sends the item's outcome as `reason`).
const REASON_WORD = {
  missing_info: "Details needed",
  needs_resubmission: "Resubmit",
  correspondence_sent: "Patient letter",
};
const SAYS_WHAT_TO_DO = new Set(["needs_resubmission", "correspondence_sent"]);

function StatusToken({ status, long, onClick }) {
  const state = (status && status.state) || "none";
  const meta = STATUS_META[state];
  // Clicking a chip deep-links the modal to this policy on the chip's own tab
  // (Breakdown vs Estimate), stopping the row's default open.
  const handle = onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined;
  if (!meta) {
    return (
      <span className="treatment-card-status-token treatment-card-status-token--none" onClick={handle}>—</span>
    );
  }
  const word = (state === "attention" && REASON_WORD[status.reason]) || meta.word;
  let text;
  if (long) {
    const full = status.date ? formatActivityDate(status.date) : null;
    text = full ? `${word} · ${full}` : word;
  } else {
    // done + attention lead with their date; requested has no date to show —
    // except a resubmission or a patient letter, whose word says what the
    // office has to do.
    const short = status.date ? formatShortDate(status.date) : null;
    text = state === "requested" ? "Req"
      : (state === "attention" && SAYS_WHAT_TO_DO.has(status.reason)) ? word
        : (short || word);
  }
  // The ⚑ on a needs-attention chip is styled separately (bigger + red) from the
  // ✓ on a done chip.
  const iconCls = meta.cls === "attn" ? "treatment-card-status-flag" : "treatment-card-status-check";
  return (
    <span
      className={`treatment-card-status-token treatment-card-status-token--${meta.cls}`}
      onClick={handle}
    >
      {meta.icon && <span className={iconCls}>{meta.icon}</span>}
      {text}
    </span>
  );
}

// One line in the Status grid for a single policy: an indicator (red ⚑ when it
// needs attention, a muted note glyph when it merely carries a note) + carrier ·
// 1°/2°, then aligned Breakdown and Estimate tokens. The policy's own note lives
// here (patient-level notes stay in the Note column): hover the carrier to read
// it, and the +N popover prints it in full. Clicking deep-links the modal to
// that policy — `onOpen(policyId, tab)` is supplied by the host surface.
function renderPolicyStatusRow(ps, long, onOpen) {
  const carrier = ps.carrier_name || ps.carrier_code || "Policy";
  const typeMark = ps.insurance_type === "primary" ? "1°" : ps.insurance_type === "secondary" ? "2°" : "";
  const tab = ps.estimate && ps.estimate.state !== "none" && (!ps.breakdown || ps.breakdown.state === "none") ? "estimates" : "breakdown";
  const note = ps.attention_note;

  // Needs-attention shows as a gold token WITH its date, in the first type that
  // isn't already done (breakdown, else estimate). If both are done yet the
  // policy is still flagged, fall back to a gold flag by the carrier.
  let bd = ps.breakdown;
  let est = ps.estimate;
  let flag = null;
  if (ps.needs_attention) {
    const attn = { state: "attention", date: ps.needs_attention_at };
    if (!bd || bd.state !== "verified") bd = attn;
    else if (!est || est.state !== "available") est = attn;
    else flag = <FlagFilled className="treatment-card-status-attn" />;
  }
  const indicator = flag
    || (!ps.needs_attention && note ? <MessageFilled className="treatment-card-status-noteicon" /> : null);

  const policySpan = (
    <span
      className="treatment-card-status-policy"
      title={note ? undefined : `${carrier}${typeMark ? " · " + typeMark : ""}`}
    >
      {indicator}
      <span className="treatment-card-status-carrier">{carrier}</span>
      {typeMark && <span className="treatment-card-status-type">{typeMark}</span>}
    </span>
  );
  const policyEl = note ? (
    <Tooltip title={note} placement="topLeft" overlayStyle={{ maxWidth: 300 }}>{policySpan}</Tooltip>
  ) : policySpan;
  const row = (
    <div
      className="treatment-card-status-row"
      onClick={(e) => { e.stopPropagation(); onOpen(ps.policy_id, tab); }}
    >
      {policyEl}
      <StatusToken status={bd} long={long} onClick={() => onOpen(ps.policy_id, "breakdown")} />
      <StatusToken status={est} long={long} onClick={() => onOpen(ps.policy_id, "estimates")} />
    </div>
  );
  // In the roomy +N popover, print the note in full beneath its policy.
  if (long && note) {
    return (
      <div key={ps.policy_id} className="treatment-card-status-longrow">
        {row}
        <div className="treatment-card-status-note">{note}</div>
      </div>
    );
  }
  return React.cloneElement(row, { key: ps.policy_id });
}

// Header; its first slot holds the "+N" expander so the two policy rows can fill
// the row height without a third line.
function headerRow(more) {
  return (
    <div className="treatment-card-status-row treatment-card-status-head">
      <span className="treatment-card-status-head-slot"><span>Policy</span>{more}</span>
      <span>Breakdown</span>
      <span>Estimate</span>
    </div>
  );
}

/**
 * Per-policy breakdown + estimate state for one patient (no rollup). Up to 2
 * policies inline; 3+ collapse behind a "+N" popover.
 *
 * Shared by the Patients board rows (TreatmentCard) and the FTC
 * Tasks table, so both read identically.
 *
 * @param policyStatuses  the patient's `policy_statuses` payload
 * @param onOpenPolicy    (policyId, tab) => void — deep-links the insurance
 *                        modal to that policy on "breakdown" | "estimates"
 */
export default function PolicyStatusCell({
  policyStatuses,
  onOpenPolicy,
  className = "treatment-card-status-cell",
  dataOnboardingId,
}) {
  const list = Array.isArray(policyStatuses) ? policyStatuses : [];
  const onOpen = typeof onOpenPolicy === "function" ? onOpenPolicy : () => {};

  // Nothing to show yet → keep the column structure with a greyed-out skeleton
  // so the row still reads as a Status cell.
  if (list.length === 0) {
    return (
      <div className={className} data-onboarding-id={dataOnboardingId}>
        {headerRow(null)}
        {[0, 1].map((i) => (
          <div key={i} className="treatment-card-status-row treatment-card-status-ph-row">
            <span className="treatment-card-status-ph treatment-card-status-ph--name" />
            <span className="treatment-card-status-ph treatment-card-status-ph--chip" />
            <span className="treatment-card-status-ph treatment-card-status-ph--chip" />
          </div>
        ))}
      </div>
    );
  }

  const rest = list.slice(2);
  const more = rest.length > 0 ? (
    <Popover
      placement="leftTop"
      trigger="click"
      overlayClassName="treatment-card-status-popover"
      content={
        <div className="treatment-card-status-grid" onClick={(e) => e.stopPropagation()}>
          <div className="treatment-card-status-poptitle">Policy status</div>
          {headerRow(null)}
          {list.map((ps) => renderPolicyStatusRow(ps, true, onOpen))}
        </div>
      }
    >
      <span className="treatment-card-status-more" onClick={(e) => e.stopPropagation()}>
        +{rest.length} more
      </span>
    </Popover>
  ) : null;

  return (
    <div className={className} data-onboarding-id={dataOnboardingId}>
      {headerRow(more)}
      {list.slice(0, 2).map((ps) => renderPolicyStatusRow(ps, false, onOpen))}
    </div>
  );
}
